import { Coins, Snowflake } from "lucide-react"
import { useTranslation } from "react-i18next"

import { Card, CardContent } from "@/components/ui/card"
import type { StreakFreezeSurface } from "@/types/streak"

/**
 * OKS-515 — §12 Streak Freeze surface (read-only).
 *
 * Informational only this ticket — it advertises that a Streak Freeze exists
 * ("skip a day without breaking · N left" + a coin-cost pill) so the creator
 * knows the safety net is coming. The freeze ACTION (spend a freeze) is
 * OKS-516; the `remaining` count + `coin_cost` here are display-only
 * placeholders and there is intentionally NO interactive CTA on the active-
 * state surface (the disabled "Use a freeze" button lives on the at-risk
 * state card).
 *
 * Decorative glyphs are `aria-hidden`; the coin cost carries an accessible
 * label.
 */
interface StreakFreezeCardProps {
  freeze: StreakFreezeSurface
}

export function StreakFreezeCard({ freeze }: StreakFreezeCardProps) {
  const { t } = useTranslation()

  return (
    <Card data-testid="streak-freeze-card">
      <CardContent className="flex flex-col gap-3 py-4">
        <h2 className="text-muted-foreground text-xs font-semibold tracking-wide">
          {t("streak.detail.freeze.section_label")}
        </h2>
        <div className="flex items-center gap-4">
          <span
            aria-hidden="true"
            className="flex size-12 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-sky-400 to-blue-500 text-white"
          >
            <Snowflake className="size-6" />
          </span>
          <div className="flex min-w-0 flex-1 flex-col">
            <span className="text-sm font-semibold">
              {t("streak.detail.freeze.title")}
            </span>
            <span className="text-muted-foreground text-sm">
              {t("streak.detail.freeze.body", { count: freeze.remaining })}
            </span>
          </div>
          <span
            className="flex shrink-0 items-center gap-1 rounded-full bg-amber-500/15 px-2.5 py-1 text-sm font-medium text-amber-700"
            aria-label={t("streak.detail.freeze.cost_aria", {
              count: freeze.coin_cost,
            })}
            data-testid="streak-freeze-cost"
          >
            <Coins aria-hidden="true" className="size-4" />
            {freeze.coin_cost}
          </span>
        </div>
      </CardContent>
    </Card>
  )
}
